import { useCallback, useEffect, useRef, useState } from "react";
import { normalizeHex } from "../lib/color";

const sanitize = (raw) => {
  let v = raw;
  if (!v.startsWith("#")) v = "#" + v.replace(/#/g, "");
  return "#" + v.slice(1).replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
};

/** Encapsulates draft state, validation, and commit logic for the hex input. */
function useHexDraft(value, onChange) {
  const [draft, setDraft] = useState(value);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setDraft(value);
    setInvalid(false);
  }, [value]);

  const onType = useCallback((e) => {
    setDraft(sanitize(e.target.value));
    setInvalid(false);
  }, []);

  const commit = useCallback(() => {
    const norm = normalizeHex(draft);
    if (!norm) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setDraft(norm);
    if (norm !== value) onChange(norm);
  }, [draft, value, onChange]);

  const revert = useCallback(() => {
    setDraft(value);
    setInvalid(false);
  }, [value]);

  return { draft, invalid, onType, commit, revert };
}

export function HexInput({ value, onChange }) {
  const ref = useRef(null);
  const { draft, invalid, onType, commit, revert } = useHexDraft(value, onChange);

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
      ref.current?.blur();
    } else if (e.key === "Escape") {
      e.preventDefault();
      revert();
      ref.current?.blur();
    }
  };

  return (
    <div className="flex items-end gap-4">
      <span className="text-xs font-medium uppercase tracking-[0.2em] text-muted pb-3">
        HEX
      </span>
      <input
        ref={ref}
        data-testid="color-input-hex"
        value={draft}
        onChange={onType}
        onBlur={commit}
        onKeyDown={onKeyDown}
        spellCheck={false}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        maxLength={7}
        className={`font-mono text-4xl md:text-6xl font-semibold tracking-tight bg-transparent border-b-2 outline-none w-[7.5ch] transition-colors ${
          invalid
            ? "border-red-500 focus:border-red-500"
            : "border-border focus:border-foreground"
        }`}
      />
    </div>
  );
}
